import React, {MouseEventHandler, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'
import styled from 'styled-components'

import {useLang} from '@_metronic/i18n/Metronici18n'
import {useAuth} from '@app/modules/auth'
import {useSubscriptionModalStore} from '@app/components/molecules/SubscriptionModal/SubscriptionModal'

import {addHostPrefixToUrl} from '@hooks/useHostBasedUrl'
import useIsPremium from '@app/hooks/useIsPremium'

import useCatalogsStore, {CatalogCodeName} from '@store/useCatalogStore'

import LabelWithImage from '@_metronic/partials/layout/editsim-drawer/EditSimCatalogParams/components/CatalogField/components/LabelWithImage'
import OptionWithImage from '@_metronic/partials/layout/editsim-drawer/EditSimCatalogParams/components/CatalogField/components/OptionWithImage'
import {KTIcon} from '@_metronic/helpers'

import ThemedSelect from '@components/atoms/ThemedSelect'

import {CatalogSkill, Skill} from '@api/models'

type EditSimSkillsProps = {
  onChange: (data: CatalogSkill | ((prevState: any) => any) | any) => void
  activeSkills: CatalogSkill[]
}

const EditSimSkills = ({onChange, activeSkills}: EditSimSkillsProps) => {
  const language = useLang()

  const intl = useIntl()
  const {loadCatalogData, catalogsData, catalogs} = useCatalogsStore()
  const catalogName = useMemo(() => {
    if (catalogs) {
      const catalog = catalogs.find((x) => x.code_name === CatalogCodeName.skills)
      if (catalog)
        return catalog.translate.name[language] ? catalog.translate.name[language] : catalog.name
    }
    return ''
  }, [catalogs])

  const catalog = catalogsData[CatalogCodeName.skills]

  const options: Skill[] = useMemo(() => {
    if (!catalog || !catalog.data || catalog.loading) return []
    else {
      return catalog.data.map((option) => {
        const {id, name, params} = option
        return {
          value: id,
          label: option.translate.name[language] ? option.translate.name[language] : name,
          imageUrl: addHostPrefixToUrl(params?.image),
        }
      })
    }
  }, [catalog, language, activeSkills])

  useEffect(() => {
    loadCatalogData(CatalogCodeName.skills)
  }, [])

  const OptionWithImageWrapper = (props) => {
    return <OptionWithImage {...props} />
  }
  const selectSkill = (skill: Skill, key: number) => {
    const skillsClone = [...activeSkills]
    skillsClone[key] = {...skillsClone[key], id: skill.value || 0}

    onChange(skillsClone)
  }

  const addSkill = () => {
    onChange((prevState) => [...prevState, {level: 0}])
  }

  const is_premium = useIsPremium()
  const freeUser = !is_premium
  const {show: showSubscriptionModal} = useSubscriptionModalStore()

  if (catalog?.loading) return <></>

  const changeSkillValue = (value: string, key: number) => {
    const skillsClone = [...activeSkills]
    skillsClone[key] = {...skillsClone[key], level: parseInt(value)}
    onChange(skillsClone)
  }
  const removeField = (key: number) => {
    const skillsClone = [...activeSkills]
    skillsClone.splice(key, 1)
    onChange(skillsClone)
  }

  return (
    <Wrapper $readonly={freeUser}>
      <div className='d-flex justify-content-between align-items-center'>
        <label className='form-label'>{catalogName} The Sims</label>
      </div>
      <div className={'d-flex flex-column gap-3'}>
        {activeSkills.map((x, key) => (
          <div key={key} className={'d-flex align-items-center gap-3'}>
            <CustomDiv>
              <ThemedSelect
                key={key}
                value={options.find((opt) => opt.value === x.id)}
                placeholder={catalogName}
                className='react-select-styled'
                classNamePrefix='react-select'
                options={options}
                components={{Option: OptionWithImageWrapper}}
                onChange={(option) => selectSkill(option as Skill, key)}
                formatOptionLabel={(value) => {
                  // console.log('### value', value)
                  // @ts-ignore
                  return <LabelWithImage {...value} />
                }}
              />
            </CustomDiv>

            <label htmlFor='customRange1' className='form-label'>
              {x.level || 0}
            </label>
            <input
              disabled={!x.id}
              style={{flex: 0.5}}
              value={x.level || 0}
              onChange={(e) => changeSkillValue(e.currentTarget.value, key)}
              type='range'
              min={0}
              max={10}
              step={1}
              className='form-range'
              id='customRange1'
            />
            <a
              className='btn btn-secondary btn-icon w-auto px-3 z-0'
              onClick={() => removeField(key)}
            >
              <KTIcon iconName='trash' className='fs-3' />
            </a>
          </div>
        ))}
      </div>

      <a
        className='mt-5 btn btn-secondary d-flex justify-content-center align-items-center'
        onClick={addSkill}
      >
        <KTIcon iconName='plus' className='fs-3' />
        {intl.formatMessage({id: 'PERSON.ADD'})}
      </a>
      {freeUser && <ContentBlockerHover onClick={showSubscriptionModal} />}
    </Wrapper>
  )
}

export default React.memo(EditSimSkills)

const CustomDiv = styled.div`
  flex: 1;
  width: 100%;
`

const Wrapper = styled.div<{$readonly?: boolean}>`
  position: relative;

  opacity: ${({$readonly}) => ($readonly ? 0.5 : 1)};
`

const ContentBlockerHover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
`
